import React from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import identity from 'lodash/identity';
import { Controller, useFormContext } from 'react-hook-form';

import { Box } from '@src/components/Box';
import { Icon } from '@src/components/Icon';
import { Label, Text } from '@src/components/Typography';
import { palette } from '@src/theme';
import { SingleSelectFieldProps } from './interface';

export const SingleSelectField = ({
  disabled,
  formatOnChange = identity,
  label,
  name,
  options,
  parseValue = identity,
  parseError = parseValue,
  placeholder = 'Select an option',
}: SingleSelectFieldProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const fieldErrors = errors[name];

  const parsedErrors = fieldErrors ? { [name]: parseError(fieldErrors) } : {};

  const hasErrors = !!parsedErrors[name];

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value: fieldValue } }) => (
        <>
          <Label size="m">{label}</Label>
          <FormControl fullWidth>
            {!fieldValue && (
              <InputLabel
                id={`${name}-select-label`}
                shrink={false}
                sx={{ left: '-5px', top: '2px', color: palette.controls.outline }}
              >
                {placeholder}
              </InputLabel>
            )}
            <Select
              disabled={disabled}
              error={!!hasErrors}
              fullWidth
              labelId={`${name}-select-label`}
              onChange={(e) => {
                const val = e.target.value;
                const newVal = formatOnChange(val, fieldValue);
                onChange(newVal);
              }}
              value={parseValue(fieldValue)}
              IconComponent={() => (
                <Box position="absolute" right={6}>
                  <Icon name="caret-down-solid" iconSize="20px" color="secondary" />
                </Box>
              )}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ErrorMessage
            name={name}
            errors={parsedErrors}
            render={({ message }) => (
              <Text size="s" color="state.error.secondary">
                {message}
              </Text>
            )}
          />
        </>
      )}
    />
  );
};
